import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
    Button,
    Center,
    HighlightedCard,
    Stack,
    Subtitle1,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';

import { useFillPrescription } from '../../../../contexts/fill/use-fill-prescription';
import { usePathwayOverride } from '../../../../hooks/use-pathway-override';
import type { PrescriptionOffer } from '../../../../hooks/use-prescription-offers';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';

export type SelectSelfPayPathCardProps = {
    offer: PrescriptionOffer | undefined;
};

export const SelectSelfPayPathCard = ({
    offer,
}: SelectSelfPayPathCardProps) => {
    const { t } = useTranslation('lilly');
    const [searchParams] = useSearchParams();

    const { drxId, medication } = useFillPrescription();
    const pathwayOverride = usePathwayOverride(medication);

    const line1 =
        offer?.headlinePriceType === 'Incentive' ||
        offer?.headlinePriceType === 'CashAssistance'
            ? t('fill.selfPay.pathSelect.cards.selfPay.line1')
            : t('fill.selfPay.pathSelect.cards.selfPay.line1NoCard');
    const line2 =
        !offer || offer.headlinePriceType === 'SelfPayPriceAtPharmacy'
            ? t('fill.pathSelect.cards.line2NoPrice')
            : offer.headlinePriceType === 'Incentive' ||
                offer.headlinePriceType === 'CashAssistance'
              ? t('fill.pathSelect.cards.line2', {
                    price: offer?.patientCost,
                })
              : t('fill.pathSelect.cards.line2PriceOnly', {
                    price: offer?.patientCost,
                });

    return (
        <HighlightedCard
            fullWidthFooter
            pinFooter
            fullHeight
            data-testid="path-select-card-self-pay"
            header={
                <Center data-testid="path-select-card-self-pay-title">
                    {t('fill.selfPay.pathSelect.cards.selfPay.heading')}
                </Center>
            }
            footer={
                <Button
                    fullWidth
                    link
                    variant={
                        pathwayOverride?.favoredPathway === 'insurance'
                            ? 'outline'
                            : 'filled'
                    }
                    to={{
                        pathname: getAbsoluteRoute('fill.drxId.root', {
                            drxId,
                        }),
                        search: `?${searchParams.toString()}`,
                    }}
                >
                    {t('fill.selfPay.pathSelect.cards.selfPay.button')}
                </Button>
            }
        >
            <Stack gap="md">
                <Subtitle2>{line1}</Subtitle2>
                <Subtitle1>{line2}</Subtitle1>
            </Stack>
        </HighlightedCard>
    );
};
